// Stav objednávky: čistý model stránky (#461, orderStatusPage.js). Z odpovědi
// API `POST /v1/{tenant}/order-status` (kontrakt search/README.md §Stav
// objednávky) skládá to, co stránka kreslí: kartu stavu, osu, blok akce,
// položky, nápovědu. Bez DOM, testuje ho tools/unit/order-status.test.mjs.
//
// Pravidla plátna docs/design/2026-10-07-stav-objednavky/ (README §Stavy):
//   · osa podle způsobu doručení: na adresu 5 kroků (… Předáno dopravci →
//     Doručeno), výdejní místo 6 (… Připraveno k vyzvednutí → Vyzvednuto),
//     prodejna 5 (bez dopravce), zrušená 2 (Přijata → Zrušeno);
//   · blok akce podle fáze: převod, karta, cíl doručení, zásilka, výdejní
//     místo, prodejna, vrácení, zrušeno;
//   · dobírka nemá krok „Čeká na platbu", platí se při převzetí;
//   · návody (`items[].files`, plátno návodů 9. 10. 2026, varianta A): odkaz u
//     položky v každém výsledku kromě zrušeného, karta pod stavem od přípravy dál
//     (dokud se čeká na platbu, patří místo platbě).
// API posílá jen fázi (`stage`), popisky dává locale; interní název stavu
// z adminu na web nikdy nejde.

import T from '../locale/cs/orderStatusPage.js';
import D from '../locale/cs/dispatchEta.js';
import SM from '../locale/cs/shippingModel.js';
import SHOP from '../../config/shop.js';
import { money, number, NBSP } from './money.js';
import { beforeCutoff, isWorkday, isHolidayIn } from './dispatchEta.js';
import { DELIVERY_SPAN_DAYS, DELIVERY_START_DAYS, FAST_RULES, nextSaturdayCarrierDay, rangeLabel } from './deliveryTimeline.js';

export const MEMORY_KEY = 'bs-os-last';
export const MEMORY_MS = 30 * 24 * 3600 * 1000;
// shodné s API (search/api/order-status.mjs ORDER_CODE_RE)
export const CODE_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,31}$/;

const RETURN_DAYS = 14;
// sobotní doručení GLS, je-li pravidlo v manifestu shopu (CZ ano, SK ne)
const SAT_RULE =
  FAST_RULES.find(function (r) {
    return r.key === 'sat';
  }) || null;

// --- vstup --------------------------------------------------------------------

/** Číslo objednávky z pole nebo odkazu (mezery a `#` pryč), jinak ''. */
export function cleanCode(raw) {
  const s = String(raw == null ? '' : raw).replace(/\s+/g, '').replace(/^#/, '');
  return CODE_RE.test(s) ? s : '';
}

/** E-mail, jak ho zákazník napsal (ořezaný), nebo '' když to e-mail není. */
export function cleanEmail(raw) {
  const s = String(raw == null ? '' : raw).trim();
  return s.length <= 254 && /^[^\s@]+@[^\s@]+$/.test(s) ? s : '';
}

// `URLSearchParams` dělá z `+` mezeru, jenže Shoptet dosazuje `#EMAIL#` do
// odkazu bez kódování a `jan+eshop@…` je platná adresa → vlastní rozbor.
function params(str) {
  const out = {};
  String(str || '')
    .replace(/^[#?]/, '')
    .split('&')
    .forEach(function (pair) {
      const i = pair.indexOf('=');
      if (i < 1) return;
      const key = pair.slice(0, i);
      let value = pair.slice(i + 1);
      try {
        value = decodeURIComponent(value);
      } catch (e) {
        /* rozbitý escape: syrová hodnota */
      }
      if (!Object.prototype.hasOwnProperty.call(out, key)) out[key] = value;
    });
  return out;
}

/**
 * Údaje z odkazu: nový fragment `#o=<kód>&e=<e-mail>` (do logů ani Refereru
 * nejde), starý dotaz doplňku dkLAB `?email=&order=` (stavové šablony do přepisu
 * e-mailů a e-maily odeslané dřív; doplněk je od 9. 10. 2026 odinstalovaný). Hlavička
 * (`common-header.html`) je z adresy smaže a předá v `window.__bsOrderPrefill`.
 * Tlačítka „Stáhnout fakturu / návody" v e-mailech přidávají před `e=` cíl posunu
 * `k=doklady|navody` (#531 U5); jiná hodnota se ignoruje a do paměti `k` nejde.
 * @returns {{code: string, email: string, k?: string}|null}
 */
export const PREFILL_TARGETS = ['doklady', 'navody'];

export function parsePrefill(hash, search) {
  const h = params(hash);
  const q = params(search);
  const code = cleanCode(h.o || q.order);
  const email = cleanEmail(h.e) || cleanEmail(hashEmailTail(hash)) || cleanEmail(q.email);
  if (!code && !email) return null;
  const out = { code: code, email: email };
  if (PREFILL_TARGETS.indexOf(h.k) >= 0) out.k = h.k;
  return out;
}

// `e=` stojí v odkazu z e-mailu poslední a Shoptet `#EMAIL#` nekóduje: adresa s `&`
// (`a&b@firma.cz`, RFC 5322 ji dovoluje) by se rozdělením na `&` usekla (review 9. 10. 2026)
function hashEmailTail(hash) {
  const m = /(?:^#?|&)e=([\s\S]*)$/.exec(String(hash || ''));
  if (!m) return '';
  try {
    return decodeURIComponent(m[1]);
  } catch (e) {
    return m[1];
  }
}

/**
 * Dotaz adresy bez starých parametrů odkazu (`order`, `email`), ostatní (utm_*)
 * zůstávají. Totéž dělá prefill skript hlavičky; tohle je záloha pro instanci,
 * jejíž HTML kódy skript ještě nemají.
 */
export function cleanSearch(search) {
  return String(search || '')
    .replace(/([?&])(order|email)=[^&]*/g, '$1')
    .replace(/([?&])&+/g, '$1')
    .replace(/[?&]$/, '');
}

// --- paměť poslední objednávky (localStorage, 30 dní) ---------------------------

export function readMemory(store, now) {
  try {
    const raw = store && store.getItem(MEMORY_KEY);
    const m = raw ? JSON.parse(raw) : null;
    const t = now == null ? Date.now() : now;
    if (!m || !cleanCode(m.code) || !cleanEmail(m.email) || !(t - Number(m.at) < MEMORY_MS)) {
      // prošlý nebo neplatný záznam pryč: číslo a e-mail nesmí v úložišti zůstat
      // déle než 30 dní (review 8. 10. 2026)
      if (raw) forgetMemory(store);
      return null;
    }
    return { code: cleanCode(m.code), email: cleanEmail(m.email) };
  } catch (e) {
    forgetMemory(store); // rozbitý JSON; zakázané úložiště zahodí forgetMemory samo
    return null;
  }
}

export function writeMemory(store, code, email, now) {
  try {
    if (store) store.setItem(MEMORY_KEY, JSON.stringify({ code: code, email: email, at: now == null ? Date.now() : now }));
  } catch (e) {
    /* plná kvóta, zakázané úložiště: bez paměti */
  }
}

export function forgetMemory(store) {
  try {
    if (store) store.removeItem(MEMORY_KEY);
  } catch (e) {
    /* zakázané úložiště */
  }
}

// --- data ---------------------------------------------------------------------

export function toDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function addDays(d, n) {
  const out = new Date(d.getTime());
  out.setDate(out.getDate() + n);
  return out;
}

function nextWorkday(d) {
  let day = startOfDay(d);
  for (let i = 0; i < 14; i += 1) {
    day = addDays(day, 1);
    if (isWorkday(day)) return day;
  }
  return addDays(startOfDay(d), 1);
}

/** „7. 10." (pevná mezera, datum se nezalomí) */
export function dayMonth(d) {
  return d.getDate() + '.' + NBSP + (d.getMonth() + 1) + '.';
}

/** „7. 10. 2026" (eyebrow karty stavu) */
export function fullDate(d) {
  return dayMonth(d) + NBSP + d.getFullYear();
}

/** „9:41" */
export function clock(d) {
  const m = d.getMinutes();
  return d.getHours() + ':' + (m < 10 ? '0' : '') + m;
}

/** „6. 10., 20:15" (meta kroku osy) */
export function stamp(d) {
  return dayMonth(d) + ', ' + clock(d);
}

/** „Aktualizováno dnes v 18:52" / „Aktualizováno 6. 10. v 18:52" */
export function updatedLabel(d, now) {
  if (!d) return '';
  return T.updated(T.updatedAt(sameDay(d, now) ? null : dayMonth(d), clock(d)));
}

// --- objednávka ---------------------------------------------------------------

function atOf(order, stage) {
  const list = order.timeline || [];
  for (let i = 0; i < list.length; i += 1) {
    if (list[i].stage === stage) return toDate(list[i].at);
  }
  return null;
}

/** Způsob doručení: `pickup` (výdejní místo), `store` (prodejna), `address`. */
export function shippingKind(order) {
  const s = order.shipping || {};
  if (s.pickup) return 'pickup';
  if (T.storeRe.test(s.method || '')) return 'store';
  // výdejní místo bez údajů pobočky: podle názvu metody (týž výraz jako pokladna a
  // ceník), jinak by osa i text přeskakovaly mezi adresou a výdejním místem (review 9. 10. 2026)
  if (SM.boxRe.test(s.method || '')) return 'pickup';
  // zásilka hlásí „k vyzvednutí"
  if (order.stage === 'ready_for_pickup') return 'pickup';
  return 'address';
}

/** Odkaz z API do `href` jen jako https (API to hlídá taky; `javascript:` ani http nikdy). */
export function httpsUrl(raw) {
  return typeof raw === 'string' && /^https:\/\/[^\s"'<>]+$/.test(raw) ? raw : null;
}

/**
 * Platba: `cod` (dobírka), `pending` (platbu ověřujeme, znovu neplatit), `unverified` (stav
 * ze zálohy při výpadku, údaje k platbě jen v e-mailu), `transfer` (převod k úhradě),
 * `card` (online odkaz), `other`.
 */
export function payKind(order) {
  if (order.cod) return 'cod';
  const p = order.payment || {};
  // stav „platba se ověřuje" (CZ 25): API pak údaje k převodu ani odkaz neposílá (QA 8. 10. 2026)
  if (p.pending === true) return 'pending';
  // výpadek Shoptetu: objednávka mohla být mezitím zaplacená, API údaje k platbě neposílá
  // a stránka odkáže na potvrzovací e-mail (Tomáš 9. 10. 2026)
  if (p.unverified === true) return 'unverified';
  if (p.transfer) return 'transfer';
  if (httpsUrl(p.online_link)) return 'card';
  return 'other';
}

function unpaid(order) {
  return order.paid !== true && !order.cod;
}

/**
 * Poslední známý stav při výpadku obnovy (síť, 503): zastaralý a bez odkazu a údajů
 * k platbě. Zákazník mohl mezitím zaplatit a stará výzva by ho vedla k druhé platbě
 * (review 9. 10. 2026; totéž API dělá u své zálohy přes `payment.unverified`).
 */
export function staleResult(data) {
  const order = Object.assign({}, data.order);
  const p = order.payment || {};
  if (unpaid(order) && p.pending !== true) order.payment = { method: p.method, unverified: true };
  return Object.assign({}, data, { order: order, stale: true });
}

// Doklad o platbě: příznak z API nebo přechod do „Zaplaceno" v historii.
function paidProof(order) {
  return order.paid === true || !!atOf(order, 'paid');
}

// Datum převzetí: ze zásilky; u osobního odběru na prodejně (zásilka není) přechod do
// Hotovo, ten prodejna dává při výdeji (QA 8. 10. 2026: lhůta na vrácení bez data)
function deliveredAt(order) {
  return atOf(order, 'delivered') || (shippingKind(order) === 'store' ? atOf(order, 'done') : null);
}

/** Dopravce `{id, name}` z kódu zásilky, jinak z názvu metody dopravy, jinak null. */
export function carrierOf(order) {
  const s = order.shipping || {};
  const t = (s.tracking || [])[0];
  let id = t && t.carrier ? String(t.carrier) : null;
  if (!id) {
    for (let i = 0; i < T.carrierRe.length; i += 1) {
      if (T.carrierRe[i][0].test(s.method || '')) {
        id = T.carrierRe[i][1];
        break;
      }
    }
  }
  if (!id) return null;
  return { id: id, name: T.carriers[id] || id.toUpperCase() };
}

// Sobotní závoz platí, jen když ho má dopravce každé zásilky (bez zásilek dopravce metody).
function satDelivery(order) {
  if (!SAT_RULE) return false;
  const tracked = ((order.shipping || {}).tracking || []).filter(function (t) {
    return t && t.carrier;
  });
  const names = tracked.length
    ? tracked.map(function (t) {
        return T.carriers[t.carrier] || String(t.carrier).toUpperCase();
      })
    : [(carrierOf(order) || {}).name];
  return names.every(function (n) {
    return !!n && SAT_RULE.re.test(n);
  });
}

// Předpoklad i před odesláním (#531 U4): e-maily potvrzení a Zaplaceno odkazují na
// přesné datum na stránce. Zaplaceno a příprava vždy, dobírka i dřív; nezaplacený
// převod a karta až po platbě, prodejna nikdy.
function beforeShipment(order) {
  if (order.stage === 'paid' || order.stage === 'preparing') return true;
  return !!order.cod && (order.stage === 'received' || order.stage === 'processing');
}

// Den odeslání jako v košíku: pracovní den před uzávěrkou (15:00, v pátek 14:30)
// ještě dnes, jinak nejbližší další pracovní den (svátky z dispatchEta)
function dispatchDay(now) {
  const today = startOfDay(now);
  return beforeCutoff(now) ? today : nextWorkday(today);
}

/**
 * Předpoklad doručení: u zásilky na cestě `deliveryStartDays` pracovních dní po předání
 * dopravci (CZ další, SK druhý pracovní den, #572), před odesláním totéž od dne odeslání
 * počítaného od `now` (`beforeShipment`). SK rozmezí `deliverySpanDays`. Prošlý termín
 * se nevypisuje.
 * @returns {{from: Date, to: Date|null}|null}
 */
export function estimateOf(order, now, spanDays, startDays) {
  let at;
  if (order.stage === 'shipped') at = atOf(order, 'shipped');
  else if (beforeShipment(order) && shippingKind(order) !== 'store') at = dispatchDay(now || new Date());
  if (!at) return null;
  const span = spanDays == null ? DELIVERY_SPAN_DAYS : spanDays;
  // sobotní doručení GLS (pravidlo `sat` manifestu): týž výpočet jako termín při
  // nákupu, balík předaný v pátek dorazí v sobotu (review 8. 10. 2026). Jen když vezou
  // GLS všechny zásilky: s PPL v objednávce celá nedorazí dřív než PPL (review 9. 10. 2026).
  const start = startDays == null ? DELIVERY_START_DAYS : startDays;
  let from = at;
  if (satDelivery(order)) from = nextSaturdayCarrierDay(at);
  else for (let i = 0; i < start; i += 1) from = nextWorkday(from);
  let to = null;
  if (span > 0) {
    to = from;
    for (let i = 0; i < span; i += 1) to = nextWorkday(to);
  }
  if ((to || from) < startOfDay(now)) return null;
  return { from: from, to: to };
}

/** „čtvrtek 8. 10." / „30. 9. – 1. 10." (dlaždice předpokladu) */
export function estimateLabel(est) {
  if (!est) return '';
  return est.to ? rangeLabel(est.from, est.to) : T.dayName[est.from.getDay()] + ' ' + dayMonth(est.from);
}

// „ve čtvrtek 8. 10." (věta karty stavu)
function estimateSentence(est) {
  if (!est) return '';
  return est.to ? rangeLabel(est.from, est.to) : D.days[est.from.getDay()] + ' ' + dayMonth(est.from);
}

// „čt 8. 10." (meta kroku osy)
function estimateShort(est) {
  if (!est) return '';
  return est.to ? rangeLabel(est.from, est.to) : T.dayAbbr[est.from.getDay()] + ' ' + dayMonth(est.from);
}

function amountOf(order) {
  const t = order.total || {};
  const v = Number(t.to_pay) > 0 ? t.to_pay : t.with_vat;
  return money(v, true) || '';
}

/** Karta stavu: `{icon, title, text}`. `chat` = stránka má chat (věty „napište nám"). */
export function statusOf(order, now, chat) {
  const kind = shippingKind(order);
  const pay = payKind(order);
  const carrier = carrierOf(order);
  const S = T.status;
  switch (order.stage) {
    case 'cancelled': {
      const at = atOf(order, 'cancelled');
      return { icon: 'close', title: S.cancelled.title, text: S.cancelled.text(at ? dayMonth(at) : '') };
    }
    case 'returned':
      return { icon: 'refresh', title: S.returned.title, text: S.returned.text };
    case 'delivered':
    case 'done': {
      const at = deliveredAt(order);
      const when = at ? D.days[at.getDay()] + ' ' + dayMonth(at) : '';
      return kind === 'address'
        ? { icon: 'check', title: S.delivered.title, text: S.delivered.text(when) }
        : { icon: 'check', title: S.pickedUp.title, text: S.pickedUp.text(when) };
    }
    case 'ready_for_pickup':
      return kind === 'store'
        ? { icon: 'store', title: S.storeReady.title, text: S.storeReady.text }
        : { icon: 'pin', title: S.pickupReady.title, text: S.pickupReady.text(carrier ? carrier.name : '') };
    case 'shipped':
      return {
        icon: 'truck',
        title: S.shipped.title,
        text: S.shipped.text(carrier ? carrier.name : '', estimateSentence(estimateOf(order, now))),
      };
    case 'paid':
    case 'preparing': {
      // „Platbu máme" jen s dokladem (příznak platby nebo přechod do Zaplaceno); dobírka
      // i nezaplacená objednávka v přípravě dostanou text bez platby (Tomáš 8. 10. 2026)
      const s = kind === 'store' ? S.preparingStore : !order.cod && paidProof(order) ? S.preparing : S.preparingCod;
      return { icon: 'package', title: s.title, text: s.text };
    }
    case 'processing':
      if (!unpaid(order) || (pay !== 'transfer' && pay !== 'card' && pay !== 'pending' && pay !== 'unverified')) {
        return { icon: 'package', title: S.processing.title, text: S.processing.text(chat) };
      }
    // nezaplacená „zpracovávaná" objednávka čeká hlavně na platbu
    // falls through
    default:
      if (unpaid(order) && pay === 'pending') return { icon: 'bank', title: S.paymentPending.title, text: S.paymentPending.text };
      if (unpaid(order) && pay === 'unverified') return { icon: 'bank', title: S.paymentUnverified.title, text: S.paymentUnverified.text };
      if (unpaid(order) && pay === 'transfer') {
        const s = order.payment.transfer.qr_svg ? S.waitTransfer : S.waitTransferNoQr;
        return { icon: 'bank', title: s.title, text: s.text };
      }
      if (unpaid(order) && pay === 'card') return { icon: 'card', title: S.waitCard.title, text: S.waitCard.text };
      return { icon: 'package', title: S.received.title, text: S.received.text };
  }
}

/**
 * Kroky osy: `[{key, label, meta, state: done|cur|todo|fail, icon}]`.
 */
export function stepsOf(order, now) {
  const created = toDate(order.created_at);
  const stampAt = function (stage) {
    const at = atOf(order, stage);
    return at ? stamp(at) : '';
  };
  const join = function (parts) {
    return parts.filter(Boolean).join(' · ');
  };

  if (order.stage === 'cancelled') {
    return [
      { key: 'received', label: T.step.received, meta: created ? stamp(created) : '', state: 'done', icon: null },
      { key: 'cancelled', label: T.step.cancelled, meta: stampAt('cancelled'), state: 'fail', icon: 'close' },
    ];
  }

  const kind = shippingKind(order);
  const pay = payKind(order);
  const carrier = carrierOf(order);
  const est = estimateOf(order, now);
  const keys =
    kind === 'store'
      ? ['received', 'payment', 'preparing', 'ready', 'picked']
      : kind === 'pickup'
        ? ['received', 'payment', 'preparing', 'shipped', 'ready', 'picked']
        : ['received', 'payment', 'preparing', 'shipped', 'delivered'];

  let cur;
  switch (order.stage) {
    case 'paid':
    case 'preparing':
      cur = 2;
      break;
    case 'shipped':
      cur = kind === 'store' ? keys.indexOf('ready') : keys.indexOf('shipped');
      break;
    case 'ready_for_pickup':
      cur = keys.indexOf('ready');
      break;
    case 'delivered':
    case 'done':
    case 'returned':
      cur = keys.length;
      break;
    default:
      // received, processing: nezaplacená objednávka čeká na platbu
      cur = unpaid(order) ? 1 : 2;
  }

  const amount = amountOf(order);
  const steps = keys.map(function (key, i) {
    const state = i < cur ? 'done' : i === cur ? 'cur' : 'todo';
    const on = state !== 'todo';
    const step = { key: key, label: '', meta: '', state: state, icon: null };
    switch (key) {
      case 'received':
        step.label = T.step.received;
        step.meta = created ? stamp(created) : '';
        break;
      case 'payment':
        if (order.cod) {
          step.label = T.step.cod;
          step.meta = T.stepMeta.cod(amount);
        } else if (state === 'cur' && pay === 'pending') {
          step.label = T.step.paymentPending;
          step.icon = 'bank';
          step.meta = join([(order.payment || {}).method, amount]);
        } else if (state === 'cur' && pay === 'unverified') {
          // stav ze zálohy: bez výzvy k platbě a bez částky (mohla se mezitím změnit)
          step.label = T.step.payment;
          step.icon = 'bank';
          step.meta = (order.payment || {}).method || '';
        } else if (state === 'cur') {
          step.label = T.step.waitPay;
          step.icon = pay === 'transfer' ? 'bank' : 'card';
          step.meta = pay === 'transfer' ? T.stepMeta.payTransfer(amount) : pay === 'card' ? T.stepMeta.payCard(amount) : T.stepMeta.payOther(amount);
        } else {
          // „Zaplaceno" jen s dokladem (příznak platby nebo přechod v historii)
          step.label = paidProof(order) ? T.step.paid : T.step.payment;
          step.meta = join([stampAt('paid'), (order.payment || {}).method]);
        }
        break;
      case 'preparing':
        step.label = T.step.preparing;
        if (on) step.meta = stampAt('preparing');
        else if (unpaid(order)) step.meta = pay === 'transfer' ? T.stepMeta.afterPayment : T.stepMeta.afterCard;
        if (state === 'cur') step.icon = 'package';
        break;
      case 'shipped':
        step.label = T.step.shipped;
        step.meta = on ? join([stampAt('shipped'), carrier && carrier.name]) : carrier ? carrier.name : '';
        if (state === 'cur') step.icon = 'truck';
        break;
      case 'ready':
        step.label = T.step.pickupReady;
        if (kind === 'store') {
          step.meta = on ? join([stampAt('ready_for_pickup'), T.stepMeta.store]) : '';
          if (state === 'cur') step.icon = 'store';
        } else {
          step.meta = on ? stampAt('ready_for_pickup') : est ? T.stepMeta.estimate(estimateShort(est)) : T.stepMeta.atPickup;
          if (state === 'cur') step.icon = 'pin';
        }
        break;
      case 'picked': {
        const at = on ? deliveredAt(order) : null;
        step.label = T.step.pickedUp;
        step.meta = at ? stamp(at) : '';
        break;
      }
      case 'delivered':
        step.label = T.step.delivered;
        step.meta = on ? stampAt('delivered') : est ? T.stepMeta.estimate(estimateShort(est)) : '';
        break;
    }
    return step;
  });
  if (order.stage === 'returned') {
    steps.push({ key: 'returned', label: T.step.returned, meta: stampAt('returned'), state: 'cur', icon: 'refresh' });
  }
  return steps;
}

/** Blok akce pod osou (plátno §Blok akce podle fáze). */
export function actionOf(order) {
  const kind = shippingKind(order);
  const pay = payKind(order);
  switch (order.stage) {
    case 'cancelled':
      return 'cancelled';
    case 'delivered':
    case 'done':
    case 'returned':
      return 'returns';
    case 'ready_for_pickup':
      return kind === 'store' ? 'store' : 'pickup';
    case 'shipped':
      return kind === 'store' ? 'dest' : 'tracking';
    case 'paid':
    case 'preparing':
      // v přípravě už platební blok ne: obchod objednávku pustil dál a tlačítko by
      // mohlo vést k dvojí platbě, nezaplacenou řeší obchod (Tomáš 8. 10. 2026)
      return 'dest';
    default:
      if (unpaid(order) && pay === 'transfer') return 'transfer';
      if (unpaid(order) && pay === 'card') return 'card';
      return 'dest';
  }
}

const FAQ = {
  transfer: ['ship', 'invoice', 'change'],
  card: ['shipPrep', 'invoice', 'change'],
  dest: ['shipPrep', 'change', 'where'],
  tracking: ['track', 'addr', 'refuse'],
  pickup: ['track', 'refuse', 'ret'],
  store: ['storeWhere', 'ret', 'warranty'],
  returns: ['ret', 'claim', 'warranty'],
  cancelled: ['shipPrep', 'pay', 'change'],
};

/**
 * Tři časté dotazy k fázi (klíče `T.faq`). `selfCancel` = stránka nabízí storno
 * (`cancelOf`): dotaz na zrušení dostane odpověď s tlačítkem místo „napište nám".
 */
export function faqOf(action, selfCancel) {
  return (FAQ[action] || FAQ.dest)
    .map(function (key) {
      return selfCancel && key === 'change' ? 'changeSelf' : key;
    })
    .filter(function (key) {
      return !!T.faq[key];
    });
}

/**
 * Panel pomoci: `pre` do odeslání (zrušit, změnit), `post` potom; `preCancel`, když zrušit
 * jde tlačítkem na stránce a panel nabízí jen změnu.
 */
export function helpOf(order, selfCancel) {
  if (selfCancel) return 'preCancel';
  return order.stage === 'received' || order.stage === 'paid' || order.stage === 'preparing' || order.stage === 'processing' ? 'pre' : 'post';
}

/**
 * Nabídnout samoobslužné storno? API pošle `cancel.allowed` jen u objednávky, kterou smí
 * zákazník zrušit sám (stav z bílého seznamu tenantu, nezaplacená, bez zásilky, čerstvý
 * záznam); `enabled` = přepínač `orderCancel` instance. Konečně rozhoduje API při zrušení.
 */
export function cancelOf(order, enabled) {
  return Boolean(enabled && order && order.cancel && order.cancel.allowed === true && order.stage !== 'cancelled');
}

/** Údaje k převodu, nebo null. `qr` = data URI SVG z API (jen CZ). */
export function transferOf(order) {
  const t = order.payment && order.payment.transfer;
  if (!t) return null;
  const svg = typeof t.qr_svg === 'string' && /^\s*(<\?xml[^>]*>\s*)?<svg[\s>]/.test(t.qr_svg) ? t.qr_svg : null;
  return {
    amount: money(t.amount, true) || '',
    amountCopy: String(Math.round(Number(t.amount) * 100) / 100).replace('.', ','),
    account: t.account || '',
    vs: t.vs || '',
    iban: String(t.iban || '').replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 '),
    bic: t.bic || '',
    qr: svg ? 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg) : null,
  };
}

/** Zásilky: `[{carrier, number, url, line}]` (line = „Na cestě, aktualizováno 7. 10. v 18:51"). */
export function trackingOf(order) {
  const fallback = carrierOf(order);
  return ((order.shipping || {}).tracking || [])
    .filter(function (t) {
      return t && t.number;
    })
    .map(function (t) {
      const carrier = t.carrier ? { id: t.carrier, name: T.carriers[t.carrier] || String(t.carrier).toUpperCase() } : fallback;
      const state = T.ship.trackState[t.status] || '';
      const at = toDate(t.updated_at);
      const line = at ? T.ship.trackUpdated(state, dayMonth(at) + ' v ' + clock(at)) : state;
      return { carrier: carrier, number: String(t.number), url: httpsUrl(t.url), line: line };
    });
}

// Svátky země obchodu (manifest): lhůta pro zákazníka se řídí zemí, ne skladem
const HOLIDAYS = (SHOP.shop && SHOP.shop.holidays) || [];

/** Připadne-li konec lhůty na sobotu, neděli nebo svátek, končí nejbližší pracovní den (§ 607 OZ, SK § 122 OZ). */
export function legalEnd(d) {
  let day = startOfDay(d);
  for (let i = 0; i < 10 && (day.getDay() === 0 || day.getDay() === 6 || isHolidayIn(day, HOLIDAYS)); i += 1) day = addDays(day, 1);
  return day;
}

/** Konec lhůty pro vrácení („pátku 16. 10."), nebo '' bez data převzetí. */
export function returnUntil(order) {
  const at = deliveredAt(order);
  if (!at) return '';
  // 14 dnů od převzetí; víkend nebo svátek na konci lhůtu prodlouží (review 9. 10. 2026)
  const d = legalEnd(addDays(startOfDay(at), RETURN_DAYS));
  return T.dayUntil[d.getDay()] + ' ' + dayMonth(d);
}


// Zboží do seznamu položek (shodné s API `PRODUCT_ITEM_TYPES`); ostatní typy (doprava,
// platba, služba, sleva, poukaz, záloha) jsou řádky souhrnu, aby řádky daly celkem
const GOODS = ['product', 'bazar', 'product-set', 'gift'];
const MINUS = '−';

// cena řádku souhrnu: nula = „zdarma", sleva se znaménkem
function linePrice(value) {
  const n = Number(value);
  if (n > 0) return money(n) || '';
  if (n < 0) return MINUS + (money(-n) || '');
  return T.items.free;
}

/**
 * Položky a souhrn: `{products, extras, shipping, billing, count, showTotal, totalLabel, total}`.
 * Zboží a dárky v seznamu (dárek se štítkem a cenou „zdarma"), doprava, platba a ostatní
 * řádky API (`type`) v `extras`, zaokrouhlení jako poslední řádek. Zrušená objednávka řádek
 * Celkem nemá: Shoptet jí při stornu cenu nuluje (QA 8. 10. 2026, „Celkem 0 Kč").
 */
export function itemsOf(order) {
  const all = order.items || [];
  const products = all
    .filter(function (it) {
      return GOODS.indexOf(it.type) >= 0;
    })
    .map(function (it) {
      const gift = it.type === 'gift';
      return {
        code: it.code || '',
        brand: gift ? [T.items.gift, it.brand].filter(Boolean).join(' · ') : it.brand || '',
        name: it.name || '',
        meta: [it.variant, it.code ? T.items.code(it.code) : ''].filter(Boolean).join(' · '),
        qty: number(it.amount == null ? 1 : it.amount),
        price: gift && !(Number(it.price_with_vat) > 0) ? T.items.free : money(it.price_with_vat, true) || '',
        image: typeof it.image_url === 'string' && /^https:\/\//.test(it.image_url) ? it.image_url : null,
        gift: gift,
        // u zrušené objednávky návod ne (plátno návodů: zrušená nic)
        manual: order.stage === 'cancelled' ? null : manualOf(it),
      };
    });
  const rowName = function (it) {
    if (it.type === 'shipping') return T.items.shipping(it.name || '');
    if (it.type === 'billing') return T.items.billing(it.name || '');
    return it.name || T.items.extra[it.type] || T.items.extra.other;
  };
  const extras = [];
  ['shipping', 'billing', null].forEach(function (type) {
    all
      .filter(function (it) {
        return type ? it.type === type : GOODS.indexOf(it.type) < 0 && it.type !== 'shipping' && it.type !== 'billing';
      })
      .forEach(function (it) {
        extras.push({ name: rowName(it), price: linePrice(it.price_with_vat) });
      });
  });
  const extra = function (type) {
    const it = all.filter(function (x) {
      return x.type === type;
    })[0];
    if (!it) return null;
    return { name: it.name || '', price: Number(it.price_with_vat) > 0 ? money(it.price_with_vat) : T.items.free };
  };
  const total = order.total || {};
  // „Celkem k úhradě" = částka k úhradě po zaokrouhlení (`to_pay`), táž jako v ose
  // a v údajích k platbě; zaplacená a zrušená celkem s DPH (review 8. 10. 2026)
  const due = order.stage !== 'cancelled' && !order.paid && Number(total.to_pay) > 0;
  // rozdíl `to_pay` a `with_vat` do jedné koruny / eura = zaokrouhlení (dobírka SK na 5 centů),
  // jinak by řádky nedaly celkem k úhradě (QA 8. 10. 2026: 107,58 € proti 107,60 €)
  const diff = Math.round((Number(total.to_pay) - Number(total.with_vat)) * 100) / 100;
  if (due && diff !== 0 && Math.abs(diff) < 1) extras.push({ name: T.items.rounding, price: (diff < 0 ? MINUS : '') + money(Math.abs(diff)) });
  return {
    products: products,
    extras: extras,
    shipping: extra('shipping'),
    billing: extra('billing'),
    count: products.length,
    showTotal: order.stage !== 'cancelled',
    totalLabel: order.stage === 'cancelled' ? T.items.total : order.paid ? T.items.totalPaid : T.items.totalToPay,
    total: money(due ? total.to_pay : total.with_vat, true) || '',
  };
}

// --- návody (soubory ke stažení z `items[].files`) -------------------------------

const FILE_KIND_RE = /^[A-Z0-9]{2,5}$/;

/** Velikost souboru „769 kB" / „1,2 MB" (jako feed Lucy `formatSize`), bez velikosti ''. */
export function fileSize(bytes) {
  const n = Number(bytes);
  if (!(n > 0)) return '';
  const kb = n / 1024;
  if (kb < 1024) return Math.max(1, Math.round(kb)) + NBSP + 'kB';
  return new Intl.NumberFormat(SHOP.shop.language, { maximumFractionDigits: 1 }).format(kb / 1024) + NBSP + 'MB';
}

// Návod položky: první https soubor z API v pořadí adminu (ostrá 9. 10. 2026: produkt
// má nejvýš jeden soubor). `file` = „PDF, 769 kB".
function manualOf(it) {
  const f = (Array.isArray(it.files) ? it.files : []).filter(function (x) {
    return x && httpsUrl(x.url);
  })[0];
  if (!f) return null;
  const kind = typeof f.kind === 'string' && FILE_KIND_RE.test(f.kind) ? f.kind : '';
  return { url: f.url, kind: kind, file: [kind, fileSize(f.size)].filter(Boolean).join(', ') };
}

// Fáze, ve kterých je objednávka za platbou (osa nestojí na kroku platby)
const PAST_PAYMENT = ['paid', 'preparing', 'shipped', 'ready_for_pickup', 'delivered', 'done', 'returned'];

/**
 * Karta „Návody k vašim produktům": `[{name, image, url, file, kind}]`, týž soubor jednou.
 * Prázdná u zrušené objednávky a dokud se čeká na platbu (osa stojí na kroku platby:
 * převod, karta, ověřování platby); od přípravy dál a u dobírky ano.
 */
export function manualsOf(order) {
  if (order.stage === 'cancelled') return [];
  if (PAST_PAYMENT.indexOf(order.stage) < 0 && unpaid(order)) return [];
  const seen = {};
  return itemsOf(order)
    .products.filter(function (p) {
      if (!p.manual || seen[p.manual.url]) return false;
      seen[p.manual.url] = true;
      return true;
    })
    .map(function (p) {
      return { name: p.name, image: p.image, url: p.manual.url, file: p.manual.file, kind: p.manual.kind };
    });
}

/** Doklady ke stažení: `[{type, code, label, cta}]`. */
export function documentsOf(order) {
  return (order.documents || [])
    .filter(function (d) {
      return d && d.code && (d.type === 'invoice' || d.type === 'credit_note');
    })
    .map(function (d) {
      const invoice = d.type === 'invoice';
      return {
        type: d.type,
        code: String(d.code),
        label: invoice ? T.items.invoice(d.code) : T.items.creditNote(d.code),
        cta: invoice ? T.items.download : T.items.downloadCredit,
      };
    });
}

